import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import FeatureLockScreen from '../components/apple-ui/FeatureLockScreen';
import MainLayoutApple from '../components/apple-ui/MainLayoutApple';
import { toast } from 'sonner';
import {
  Users, UserPlus, Crown, Trash2, Mail, Calendar, Clock,
  ArrowLeft, RefreshCw, Settings, ChevronDown, AlertTriangle,
  CheckCircle, XCircle, Copy, ExternalLink, BookOpen, Database, Plus, X, BarChart2
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeamManagementPage(props) {
  const navigate = useNavigate();
  const teamAccess = useFeatureAccess('team');
  const [team, setTeam] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Shared resources
  const [sharedTM, setSharedTM] = useState([]);
  const [sharedGlossary, setSharedGlossary] = useState([]);
  const [contributorStats, setContributorStats] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [newGlossaryTerm, setNewGlossaryTerm] = useState({ source: '', target: '', notes: '' });
  const [addingTerm, setAddingTerm] = useState(false);
  const [showAddTerm, setShowAddTerm] = useState(false);

  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [invitationToCancel, setInvitationToCancel] = useState(null);
  
  // Form states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('translator');
  const [teamName, setTeamName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createTeamName, setCreateTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);

  const { isDarkMode } = useTheme();
  const token = localStorage.getItem('token');

  const fetchTeamData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Fetch team details
      const teamRes = await axios.get(`${API}/api/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeam(teamRes.data);
      setTeamName(teamRes.data.team_name);

      // Fetch usage stats
      const usageRes = await axios.get(`${API}/api/team/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(usageRes.data);

      // Fetch pending invitations
      const invRes = await axios.get(`${API}/api/team/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(invRes.data.invitations || []);

      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('no_team');
      } else if (err.response?.status === 403) {
        setError('not_owner');
      } else {
        setError('unknown');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  useEffect(() => {
    if (team) fetchSharedResources();
  }, [team]);

  const fetchSharedResources = async () => {
    if (!token) return;
    try {
      const [tmRes, glossRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/team/tm`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/team/glossary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/team/contributor-stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
      ]);
      setSharedTM(tmRes.data.translation_memory || []);
      setSharedGlossary(glossRes.data.glossary?.terms || []);
      if (statsRes) setContributorStats(statsRes.data);
    } catch (_) {}
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!createTeamName.trim()) return;
    setCreatingTeam(true);
    try {
      await axios.post(
        `${API}/api/team/create`,
        { team_name: createTeamName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Team created successfully!');
      setCreateTeamName('');
      fetchTeamData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/team/invite`,
        { email: inviteEmail.trim(), role: inviteRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('translator');
      setShowInviteModal(false);
      fetchTeamData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    setSubmitting(true);
    try {
      await axios.delete(
        `${API}/api/team/members/${memberToRemove.user_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${memberToRemove.name || memberToRemove.email} has been removed`);
      setMemberToRemove(null);
      fetchTeamData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelInvitation = async () => {
    if (!invitationToCancel) return;

    setSubmitting(true);
    try {
      await axios.delete(
        `${API}/api/team/invitations/${invitationToCancel.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invitation cancelled');
      setInvitationToCancel(null);
      fetchTeamData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) return;

    setSubmitting(true);
    try {
      await axios.patch(
        `${API}/api/team/settings`,
        { team_name: teamName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Team settings updated');
      setShowSettingsModal(false);
      fetchTeamData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.patch(
        `${API}/api/team/members/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Role updated');
      fetchTeamData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleAddGlossaryTerm = async (e) => {
    e.preventDefault();
    if (!newGlossaryTerm.source.trim() || !newGlossaryTerm.target.trim()) return;
    setAddingTerm(true);
    try {
      await axios.post(`${API}/api/team/glossary/add`, newGlossaryTerm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Term added to shared glossary');
      setNewGlossaryTerm({ source: '', target: '', notes: '' });
      setShowAddTerm(false);
      fetchSharedResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add term');
    } finally {
      setAddingTerm(false);
    }
  };

  const handleDeleteGlossaryTerm = async (termId) => {
    try {
      await axios.delete(`${API}/api/team/glossary/${termId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Term removed');
      fetchSharedResources();
    } catch (err) {
      toast.error('Failed to remove term');
    }
  };

  const ROLE_CONFIG = {
    owner:      { label: 'Owner',      color: 'bg-amber-500 text-white' },
    pm:         { label: 'PM',         color: 'bg-blue-500 text-white' },
    translator: { label: 'Translator', color: 'bg-green-500 text-white' },
    reviewer:   { label: 'Reviewer',   color: 'bg-purple-500 text-white' },
    member:     { label: 'Translator', color: 'bg-green-500 text-white' },
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Plan check
  if (!teamAccess.hasAccess) {
    return <FeatureLockScreen feature="team" requiredPlan={teamAccess.upgradeTo || 'business'} currentPlan={teamAccess.currentPlan} />;
  }

  // Loading state
  if (loading) {
    return (
      <MainLayoutApple {...props}>
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
            <p className="text-zinc-600">Loading team data...</p>
          </div>
        </div>
      </MainLayoutApple>
    );
  }

  // No team state — user has Business plan but hasn't created a team yet
  if (error === 'no_team') {
    return (
      <MainLayoutApple {...props}>
        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Translator
            </Button>

            <Card className="py-10">
              <CardContent>
                <div className="text-center mb-8">
                  <Users className="h-16 w-16 mx-auto text-indigo-400 mb-4" />
                  <h2 className="text-2xl font-bold text-zinc-800 mb-2">
                    Create Your Team
                  </h2>
                  <p className="text-zinc-600 max-w-md mx-auto">
                    You're on the Business plan. Set up your team workspace to start collaborating with up to 3 members.
                  </p>
                </div>
                <form onSubmit={handleCreateTeam} className="max-w-sm mx-auto space-y-4">
                  <div>
                    <Label htmlFor="create-team-name">Team Name</Label>
                    <Input
                      id="create-team-name"
                      placeholder="e.g. Localization Team"
                      value={createTeamName}
                      onChange={(e) => setCreateTeamName(e.target.value)}
                      disabled={creatingTeam}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={!createTeamName.trim() || creatingTeam}
                  >
                    {creatingTeam ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Team
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </MainLayoutApple>
    );
  }

  // Not owner state
  if (error === 'not_owner') {
    return (
      <MainLayoutApple {...props}>
        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Translator
            </Button>

            <Card className="text-center py-12">
              <CardContent>
                <Users className="h-16 w-16 mx-auto text-zinc-300 mb-4" />
                <h2 className="text-2xl font-bold text-zinc-800 mb-2">
                  Team Member View
                </h2>
                <p className="text-zinc-600 mb-6">
                  You are a member of a team. Contact your team owner to manage team settings.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </MainLayoutApple>
    );
  }

  // Unknown error state — backend down / token expired / 5xx
  if (error === 'unknown') {
    return (
      <MainLayoutApple {...props}>
        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Translator
            </Button>

            <Card className="text-center py-12">
              <CardContent>
                <AlertTriangle className="h-16 w-16 mx-auto text-amber-400 mb-4" />
                <h2 className="text-2xl font-bold text-zinc-800 mb-2">
                  Failed to Load Team Data
                </h2>
                <p className="text-zinc-600 mb-6 max-w-md mx-auto">
                  Unable to connect to server. Please check your connection and try again.
                </p>
                <Button onClick={() => fetchTeamData()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </MainLayoutApple>
    );
  }

  // Main team management view
  return (
    <MainLayoutApple {...props}>
    <div className="p-6" data-testid="team-management-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              data-testid="back-to-translator-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-zinc-800 flex items-center gap-2">
                <Users className="h-6 w-6 text-indigo-600" />
                {team?.team_name}
              </h1>
              <p className="text-sm text-zinc-500">Business Plan • Team Management</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSettingsModal(true)}
              data-testid="team-settings-btn"
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <Button
              onClick={() => setShowInviteModal(true)}
              disabled={team?.member_count >= team?.max_members}
              data-testid="invite-member-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          </div>
        </div>

        {/* Usage Overview Card */}
        <Card className="mb-6" data-testid="team-usage-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Team Usage</span>
              <Badge variant={team?.usage_percent >= 80 ? 'destructive' : 'secondary'}>
                {team?.usage_percent?.toFixed(0)}% used
              </Badge>
            </CardTitle>
            <CardDescription>
              Shared quota across all team members • Resets on {usage?.usage_reset_date ? formatDate(usage.usage_reset_date) : 'N/A'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Progress 
                value={team?.usage_percent || 0} 
                className="h-3"
                data-testid="usage-progress-bar"
              />
              
              <div className="flex justify-between text-sm">
                <span className="text-zinc-600">
                  {formatNumber(team?.total_usage || 0)} words used
                </span>
                <span className="text-zinc-600">
                  {formatNumber(team?.remaining_quota || 0)} remaining
                </span>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-indigo-600">
                    {formatNumber(team?.monthly_quota || 500000)}
                  </p>
                  <p className="text-xs text-zinc-500">Monthly Quota</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">
                    {team?.member_count || 1}
                  </p>
                  <p className="text-xs text-zinc-500">Team Members</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">
                    {usage?.days_until_reset || 0}
                  </p>
                  <p className="text-xs text-zinc-500">Days Until Reset</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage by Member */}
        {usage?.usage_by_member && (
          <Card className="mb-6" data-testid="member-usage-card">
            <CardHeader>
              <CardTitle>Usage by Member</CardTitle>
              <CardDescription>Individual word usage within shared quota</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {usage.usage_by_member.map((member) => (
                  <div 
                    key={member.user_id} 
                    className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-[#1e293b]' : 'bg-zinc-50'}`}
                    data-testid={`member-usage-${member.email}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        member.role === 'owner' ? 'bg-amber-100' : 'bg-indigo-100'
                      }`}>
                        {member.role === 'owner' ? (
                          <Crown className="h-5 w-5 text-amber-600" />
                        ) : (
                          <Users className="h-5 w-5 text-indigo-600" />
                        )}
                      </div>
                      <div>
                        <span className="font-medium text-zinc-800">
                          {member.name || member.email}
                          {member.role === 'owner' && (
                            <Badge variant="outline" className="ml-2 text-xs">Owner</Badge>
                          )}
                        </span>
                        <p className="text-sm text-zinc-500">{member.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-zinc-800">
                        {formatNumber(member.usage)} words
                      </p>
                      <p className="text-xs text-zinc-500">
                        {member.percent?.toFixed(1)}% of quota
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Team Members */}
        <Card className="mb-6" data-testid="team-members-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Team Members</span>
              <span className="text-sm font-normal text-zinc-500">
                {team?.member_count} / {team?.max_members} members
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Owner */}
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Crown className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <span className="font-semibold text-zinc-800 flex items-center">
                      {team?.owner_name || 'Owner'}
                      <Badge className="ml-2 bg-amber-500">Owner</Badge>
                    </span>
                    <p className="text-sm text-zinc-600">{team?.owner_email}</p>
                  </div>
                </div>
              </div>

              {/* Members */}
              {team?.members?.filter(m => m.status === 'active').map((member) => {
                const roleCfg = ROLE_CONFIG[member.role] || ROLE_CONFIG.translator;
                return (
                  <div
                    key={member.user_id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${isDarkMode ? 'bg-[#1e293b] border-gray-700' : 'bg-white border-gray-200'}`}
                    data-testid={`team-member-${member.email}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div>
                        <span className="font-semibold text-zinc-800 flex items-center gap-2">
                          {member.name || 'Team Member'}
                          <Badge className={`text-xs ${roleCfg.color}`}>{roleCfg.label}</Badge>
                        </span>
                        <p className="text-sm text-zinc-600">{member.email}</p>
                        <p className="text-xs text-zinc-400">
                          Joined {formatDate(member.joined_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Role change — owner only */}
                      {team?.owner_id === team?.owner_id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="text-xs" data-testid={`role-menu-${member.email}`}>
                              Change Role <ChevronDown className="ml-1 h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            {['pm', 'translator', 'reviewer'].map(r => (
                              <DropdownMenuItem
                                key={r}
                                onClick={() => handleChangeRole(member.user_id, r)}
                                className={member.role === r ? 'font-semibold' : ''}
                              >
                                {ROLE_CONFIG[r]?.label || r}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setMemberToRemove(member)}
                        data-testid={`remove-member-${member.email}-btn`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}

              {/* Empty state */}
              {(!team?.members || team.members.filter(m => m.status === 'active').length === 0) && (
                <div className="text-center py-8 text-zinc-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                  <p>No team members yet.</p>
                  <p className="text-sm">Invite members to share your quota.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <Card data-testid="pending-invitations-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-zinc-500" />
                Pending Invitations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {invitations.map((inv) => (
                  <div 
                    key={inv.id} 
                    className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-[#1e293b]' : 'bg-zinc-50'}`}
                    data-testid={`invitation-${inv.email}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center">
                        <Mail className="h-5 w-5 text-zinc-500" />
                      </div>
                      <div>
                        <p className="font-medium text-zinc-800">{inv.email}</p>
                        <p className="text-xs text-zinc-500">
                          Expires {formatDate(inv.expires_at)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => setInvitationToCancel(inv)}
                      data-testid={`cancel-invitation-${inv.email}-btn`}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Shared Team Glossary */}
        <Card className="mt-6" data-testid="shared-glossary-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-zinc-500" />
                Shared Glossary
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-normal text-zinc-500">{sharedGlossary.length} terms</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAddTerm(v => !v)}
                  data-testid="add-glossary-term-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Term
                </Button>
              </div>
            </CardTitle>
            <CardDescription>Terms shared across all team projects</CardDescription>
          </CardHeader>
          <CardContent>
            {showAddTerm && (
              <form onSubmit={handleAddGlossaryTerm} className={`p-4 rounded-lg mb-4 space-y-3 ${isDarkMode ? 'bg-[#1e293b]' : 'bg-zinc-50'}`} data-testid="add-term-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block">Source Term</Label>
                    <input
                      className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                      placeholder="e.g., translation"
                      value={newGlossaryTerm.source}
                      onChange={e => setNewGlossaryTerm(p => ({ ...p, source: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Target Term</Label>
                    <input
                      className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                      placeholder="e.g., अनुवाद"
                      value={newGlossaryTerm.target}
                      onChange={e => setNewGlossaryTerm(p => ({ ...p, target: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <input
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                  placeholder="Notes (optional)"
                  value={newGlossaryTerm.notes}
                  onChange={e => setNewGlossaryTerm(p => ({ ...p, notes: e.target.value }))}
                />
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowAddTerm(false)}>Cancel</Button>
                  <Button type="submit" size="sm" disabled={addingTerm} data-testid="save-term-btn">
                    {addingTerm ? 'Saving...' : 'Add Term'}
                  </Button>
                </div>
              </form>
            )}

            {sharedGlossary.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-6">No shared glossary terms yet.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {sharedGlossary.map(term => (
                  <div key={term.id || term.source} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-[#1e293b]' : 'bg-zinc-50'}`}>
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <span className="font-medium text-zinc-800 truncate">{term.source}</span>
                      <span className="text-zinc-400 text-xs">→</span>
                      <span className="text-zinc-700 truncate">{term.target}</span>
                      {term.notes && <span className="text-xs text-zinc-400 truncate">{term.notes}</span>}
                    </div>
                    {term.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 flex-shrink-0"
                        onClick={() => handleDeleteGlossaryTerm(term.id)}
                        data-testid={`delete-term-${term.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shared Team TM Stats */}
        <Card className="mt-6" data-testid="shared-tm-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-zinc-500" />
              Shared Translation Memory
            </CardTitle>
            <CardDescription>Entries available to all team members during translation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-indigo-600">{sharedTM.length}</p>
                <p className="text-xs text-zinc-500 mt-1">Total Entries</p>
              </div>
              <div className="text-sm text-zinc-500">
                Team TM entries are automatically merged into project TM search results for all team members.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Project Contributions */}
        {contributorStats && (
          <Card className="mt-6" data-testid="contributor-stats-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart2 className="h-5 w-5 text-zinc-500" />
                Project Contributions
              </CardTitle>
              <CardDescription>Words confirmed by each member across team projects</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Totals by member */}
              {contributorStats.totals_by_member?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Overall totals</p>
                  <div className="flex flex-wrap gap-3">
                    {contributorStats.totals_by_member.map(m => (
                      <div key={m.email} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-50'}`}>
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700">
                          {(m.name || m.email)[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{m.name || m.email}</p>
                          <p className="text-xs text-zinc-500">{m.words.toLocaleString()} words</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-project breakdown */}
              {contributorStats.projects?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">By project</p>
                  <div className="space-y-2">
                    {contributorStats.projects.map(proj => {
                      const wfBadge = {
                        translating: 'bg-blue-100 text-blue-700',
                        ready_for_review: 'bg-yellow-100 text-yellow-700',
                        in_review: 'bg-purple-100 text-purple-700',
                        approved: 'bg-green-100 text-green-700',
                        rejected: 'bg-red-100 text-red-700',
                      }[proj.workflow_status] || 'bg-zinc-100 text-zinc-600';

                      return (
                        <div key={proj.project_id} className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-zinc-700' : 'border-zinc-200'}`}>
                          <button
                            className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50 transition-colors ${isDarkMode ? 'hover:bg-zinc-800' : ''}`}
                            onClick={() => setExpandedProject(expandedProject === proj.project_id ? null : proj.project_id)}
                          >
                            <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{proj.project_name}</span>
                            <div className="flex items-center gap-2">
                              {proj.workflow_status && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${wfBadge}`}>
                                  {proj.workflow_status.replace(/_/g, ' ')}
                                </span>
                              )}
                              <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${expandedProject === proj.project_id ? 'rotate-180' : ''}`} />
                            </div>
                          </button>
                          {expandedProject === proj.project_id && proj.contributors?.length > 0 && (
                            <div className={`border-t px-4 py-3 space-y-1 ${isDarkMode ? 'border-zinc-700 bg-zinc-900' : 'border-zinc-100 bg-zinc-50'}`}>
                              {proj.contributors.map(c => (
                                <div key={c.email} className="flex items-center justify-between text-sm">
                                  <span className="text-zinc-700 dark:text-zinc-300">{c.name || c.email}</span>
                                  <span className="font-medium text-zinc-800 dark:text-zinc-200">{c.words.toLocaleString()} words</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {expandedProject === proj.project_id && (!proj.contributors || proj.contributors.length === 0) && (
                            <div className={`border-t px-4 py-3 text-sm text-zinc-400 ${isDarkMode ? 'border-zinc-700' : 'border-zinc-100'}`}>
                              No confirmed words yet.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(!contributorStats.totals_by_member?.length && !contributorStats.projects?.length) && (
                <p className="text-sm text-zinc-400">No contribution data yet. Start confirming segments in team projects.</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Invite Member Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new member to your team.
              They will have access to your shared quota and all premium features.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteMember}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background"
                  data-testid="invite-role-select"
                >
                  <option value="pm">PM — can invite members and assign projects</option>
                  <option value="translator">Translator — can translate assigned projects</option>
                  <option value="reviewer">Reviewer — can review and run quality checks</option>
                </select>
              </div>
              <div className={`p-3 rounded-lg text-sm ${isDarkMode ? 'bg-[#1e293b] text-blue-300' : 'bg-indigo-50 text-indigo-800'}`}>
                <p className="font-medium mb-1">What they'll get:</p>
                <ul className={`list-disc list-inside space-y-1 ${isDarkMode ? 'text-blue-400' : 'text-indigo-700'}`}>
                  <li>Access to 500,000 shared words/month</li>
                  <li>All premium features (CAT Tool, MTPE, TQA)</li>
                  <li>Unlimited TQA reports</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="send-invite-btn">
                {submitting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent data-testid="settings-modal">
          <DialogHeader>
            <DialogTitle>Team Settings</DialogTitle>
            <DialogDescription>
              Update your team configuration.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateSettings}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="teamName">Team Name</Label>
                <Input
                  id="teamName"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  required
                  data-testid="team-name-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSettingsModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-settings-btn">
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent data-testid="remove-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{memberToRemove?.name || memberToRemove?.email}</strong> from your team.
              Their account will revert to the Free plan and they will lose access to your shared quota.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-remove-btn"
            >
              {submitting ? 'Removing...' : 'Remove Member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Invitation Confirmation */}
      <AlertDialog open={!!invitationToCancel} onOpenChange={() => setInvitationToCancel(null)}>
        <AlertDialogContent data-testid="cancel-invitation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the invitation sent to <strong>{invitationToCancel?.email}</strong>.
              They will not be able to join your team using this invitation link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Invitation</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelInvitation}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-invitation-btn"
            >
              Cancel Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </MainLayoutApple>
  );
}
